import { Wordmark } from "./wordmark";
import { AppVersion } from "./app-version";
import type { BrandConfig } from "@/config/brand";
import { AI_ASSIST_ENABLED } from "@/config/features";
import "./site-footer.css";

/**
 * THE global footer — every page uses this ONE component. There used to be
 * two: this one (built for the landing page) and a second, plainer one
 * (`SiteFooter` in site-chrome.tsx) reused by every other route. That was a
 * visible inconsistency (a user could see the footer change shape between
 * `/` and `/support/`) and, worse, the two had drifted into duplicate
 * "email support" mailto links that got fixed in one place and missed in the
 * other (FAIL-442). Per Justin's call, this design wins — moved out of the
 * landing-only `sections.tsx` (which is "use client" for unrelated reasons;
 * this component needs no client APIs) into `components/` so every layout
 * can render it. The old `site-chrome.tsx` SiteFooter is gone; nothing
 * imports it anymore.
 *
 * Product links point at the `/features/` pages (MAP-146); no home-page
 * anchors here anymore.
 */

export interface SiteFooterLegal {
  legalEntity: string;
  supportEmail: string;
  tosUrl: string;
  privacyUrl: string;
  cookiesUrl?: string;
  dpaUrl?: string;
  acceptableUseUrl?: string;
  /** The /support page (contact form / privacy + data-request intake). */
  supportUrl?: string;
}

export interface SiteFooterProps {
  brandName: string;
  wordmark: { text: string; logoUrl: string | null };
  /** Map tile attribution (OSM/Protomaps credit) — shown on every page, not
   *  just where a map renders (the landing page always has too — see module doc). */
  attribution: string;
  legal?: SiteFooterLegal;
  /** FAIL-442: gates the raw "Email support" mailto to signed-in visitors. */
  signedIn?: boolean;
  /** Drops the brand/Product columns — the public shared-map viewer, where
   *  the map stays the hero and there's no global header either. */
  minimal?: boolean;
  /**
   * The square brand mark (`brand.logoUrl`, e.g. `/brand/logo.svg`) shown next
   * to the wordmark in the brand column. Optional and additive — omitted
   * entirely just renders the wordmark alone, same as before. Deliberately
   * NOT threaded through `wordmark.logoUrl` (which SiteHeader also reads for
   * its OWN separate inline pin icon — turning that on here would double up
   * the pin in the header instead).
   */
  markUrl?: string;
}

export function SiteFooter({
  brandName,
  wordmark,
  attribution,
  legal,
  signedIn = false,
  minimal = false,
  markUrl,
}: SiteFooterProps) {
  const year = new Date().getFullYear();
  return (
    <footer className="pz-sitefooter">
      <div className="pz-sitefooter__inner">
        <div className="pz-sitefooter__grid">
          {!minimal && (
            <div>
              <div className="pz-sitefooter__mark-row">
                {markUrl && (
                  // eslint-disable-next-line @next/next/no-img-element -- config-driven brand asset, fixed small size
                  <img className="pz-sitefooter__mark" src={markUrl} alt="" aria-hidden="true" />
                )}
                <Wordmark brandName={brandName} wordmark={wordmark} />
              </div>
              <p className="pz-sitefooter__tagline">
                Beautiful, shareable maps from the spreadsheets you already have.
              </p>
            </div>
          )}

          {!minimal && (
            <nav aria-label="Product">
              <h3 className="pz-sitefooter__h">Product</h3>
              <div className="pz-sitefooter__links">
                <a className="pz-sitefooter__link" href="/">
                  Make a map
                </a>
                <a className="pz-sitefooter__link" href="/features/spreadsheet-to-map/">
                  Spreadsheet to map
                </a>
                <a className="pz-sitefooter__link" href="/features/map-styling/">
                  Map styling
                </a>
                {/* AI/NL "Ask your map" hidden for launch (AI_ASSIST_ENABLED, MAP-135);
                    its target page is gated off too. */}
                {AI_ASSIST_ENABLED && (
                  <a className="pz-sitefooter__link" href="/features/ask-your-map/">
                    Ask your map
                  </a>
                )}
                <a className="pz-sitefooter__link" href="/features/live-sync/">
                  Live sync
                </a>
                <a className="pz-sitefooter__link" href="/features/territories/">
                  Territories &amp; demographics
                </a>
                <a className="pz-sitefooter__link" href="/features/sharing/">
                  Sharing &amp; embedding
                </a>
                <a className="pz-sitefooter__link" href="/features/sales-mapping/">
                  Sales mapping
                </a>
                <a className="pz-sitefooter__link" href="/features/">
                  All features
                </a>
                <a className="pz-sitefooter__link" href="/maps/">
                  Browse maps
                </a>
                <a className="pz-sitefooter__link" href="/help/developer-api/">
                  Developer API
                </a>
              </div>
            </nav>
          )}

          {legal && (
            <nav aria-label="Support">
              <h3 className="pz-sitefooter__h">Support</h3>
              <div className="pz-sitefooter__links">
                <a className="pz-sitefooter__link" href="/about/">
                  About
                </a>
                <a className="pz-sitefooter__link" href="/help/">
                  Docs
                </a>
                <a className="pz-sitefooter__link" href="/resources/">
                  Resources
                </a>
                {legal.supportUrl && (
                  <a className="pz-sitefooter__link" href={legal.supportUrl}>
                    Help &amp; data requests
                  </a>
                )}
                {signedIn && (
                  <a className="pz-sitefooter__link" href={`mailto:${legal.supportEmail}`}>
                    Email support
                  </a>
                )}
              </div>
            </nav>
          )}

          {legal && (
            <nav aria-label="Legal">
              <h3 className="pz-sitefooter__h">Legal</h3>
              <div className="pz-sitefooter__links">
                <a className="pz-sitefooter__link" href={legal.tosUrl}>
                  Terms
                </a>
                <a className="pz-sitefooter__link" href={legal.privacyUrl}>
                  Privacy
                </a>
                {legal.cookiesUrl && (
                  <a className="pz-sitefooter__link" href={legal.cookiesUrl}>
                    Cookies
                  </a>
                )}
                {legal.dpaUrl && (
                  <a className="pz-sitefooter__link" href={legal.dpaUrl}>
                    DPA
                  </a>
                )}
                {legal.acceptableUseUrl && (
                  <a className="pz-sitefooter__link" href={legal.acceptableUseUrl}>
                    Acceptable Use
                  </a>
                )}
                <a className="pz-sitefooter__link" href="/sitemap/">
                  Sitemap
                </a>
              </div>
            </nav>
          )}
        </div>

        <div className="pz-sitefooter__bar">
          <span className="pz-sitefooter__legal">
            © {year} {legal?.legalEntity ?? brandName} · All rights reserved
          </span>
          <span className="pz-sitefooter__barmeta">
            <AppVersion />
            <span className="pz-sitefooter__attribution">{attribution}</span>
          </span>
        </div>
      </div>
    </footer>
  );
}

/** One shared shape for every layout's `legal` prop — every caller was
 *  hand-building this same object from the theme (drift risk). */
export function buildSiteFooterLegal(brand: BrandConfig): SiteFooterLegal {
  return {
    legalEntity: brand.legalEntity,
    supportEmail: brand.supportEmail,
    tosUrl: brand.legalUrls.tos,
    privacyUrl: brand.legalUrls.privacy,
    cookiesUrl: brand.legalUrls.cookies,
    dpaUrl: brand.legalUrls.dpa,
    acceptableUseUrl: brand.legalUrls.acceptableUse,
    supportUrl: "/support/",
  };
}
